import { Link, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import Landing from './pages/Landing';
import Dashboard from './pages/Dashboard';
import CreateApp from './pages/CreateApp';
import AppDetail from './pages/AppDetail';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Settings from './pages/Settings';
import Upgrade from './pages/Upgrade';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import { useAuth } from './lib/auth';
import { SUPPORT_EMAIL } from './lib/config';
import { useI18n } from './i18n';
import type { TranslationKey } from './i18n/translations';
import LangSwitch from './components/LangSwitch';

/** Cloupak 几何 Logo 标记（立方体线条，呼应原型的几何装饰风格） */
function LogoMark() {
  return (
    <span className="logo-mark" aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={2.5} strokeLinecap="round">
        <path d="M12 3L4 7v10l8 4 8-4V7l-8-4z" />
        <path d="M12 3v18M4 7l8 4 8-4" />
      </svg>
    </span>
  );
}

/** 将 plan 字段映射为翻译键 */
function planKey(plan: string): TranslationKey {
  switch (plan) {
    case 'pro':
      return 'plan.pro';
    case 'enterprise':
      return 'plan.enterprise';
    default:
      return 'plan.free';
  }
}

/** 取用户名首字符（大写）作为头像占位文字 */
function avatarText(name: string): string {
  return name.trim().charAt(0).toUpperCase() || 'U';
}

/** 路由守卫：未登录重定向到 /login，校验中显示加载态 */
function ProtectedRoute({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const { t } = useI18n();
  if (!user && loading) {
    return (
      <div className="page-wrap">
        <div className="state-box">{t('common.loading')}</div>
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

export default function App() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const inWorkspace = !!user && location.pathname !== '/' && location.pathname !== '/privacy' && location.pathname !== '/terms';
  const navigation: { to: string; key: TranslationKey; active: boolean }[] = [
    { to: '/dashboard', key: 'nav.apps', active: location.pathname === '/dashboard' || location.pathname === '/create' || location.pathname.startsWith('/apps/') },
    { to: '/settings#billing', key: 'workspace.billingNav', active: location.pathname === '/upgrade' || (location.pathname === '/settings' && location.hash === '#billing') },
    { to: '/settings', key: 'nav.settings', active: location.pathname === '/settings' && location.hash !== '#billing' },
  ];

  function handleLogout() {
    logout();
    navigate('/', { replace: true });
  }

  return (
    <div className={'app-shell' + (inWorkspace ? ' workspace-shell' : '')}>
      <a className="skip-link" href="#main-content" onClick={(event) => {
        event.preventDefault();
        document.getElementById('main-content')?.focus();
      }}>{t('workspace.skipContent')}</a>
      <header className="topbar">
        <div className="topbar-inner">
          <Link to="/" className="brand" aria-label={t('app.brandAria')}>
            <LogoMark />
            <span className="brand-name">Cloupak</span>
          </Link>

          <nav className="nav" aria-label={t('app.navAria')}>
            {user && (
              <>
                {navigation.map((item) => (
                  <Link key={item.to} to={item.to} className={`nav-link${item.active ? ' active' : ''}`} aria-current={item.active ? 'page' : undefined}>
                    {t(item.key)}
                  </Link>
                ))}
              </>
            )}
          </nav>

          <div className="topbar-right">
            <LangSwitch />
            {user ? (
              <>
                <span className="plan-pill">{t(planKey(user.plan))}</span>
                <span className="avatar" title={user.name}>
                  {avatarText(user.name)}
                </span>
                <button className="btn btn-ghost btn-sm" onClick={handleLogout}>
                  {t('nav.logout')}
                </button>
              </>
            ) : (
              <Link to="/login" className="btn btn-primary btn-sm">
                {t('nav.login')}
              </Link>
            )}
          </div>
        </div>
      </header>

      <main className="content" id="main-content" tabIndex={-1}>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/create"
            element={
              <ProtectedRoute>
                <CreateApp />
              </ProtectedRoute>
            }
          />
          <Route
            path="/apps/:id"
            element={
              <ProtectedRoute>
                <AppDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/upgrade"
            element={
              <ProtectedRoute>
                <Upgrade />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>

      <footer className="footer">
        <span>Cloupak · {t('app.footerTagline')}</span>
        <span className="footer-dot">·</span>
        <Link to="/privacy">{t('footer.privacy')}</Link>
        <span className="footer-dot">·</span>
        <Link to="/terms">{t('footer.terms')}</Link>
        <span className="footer-dot">·</span>
        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
        <span className="footer-dot">·</span>
        <span>© 2026 Cloupak</span>
      </footer>
    </div>
  );
}

function NotFound() {
  const { t } = useI18n();
  return (
    <div className="page-wrap">
      <div className="empty-state">
        <div className="empty-icon">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#6c6c7d" strokeWidth={1.5} strokeLinecap="round">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 8v4M12 16v.01" />
          </svg>
        </div>
        <h2>{t('notFound.title')}</h2>
        <p>{t('notFound.desc')}</p>
        <Link to="/dashboard" className="btn btn-primary">
          {t('notFound.back')}
        </Link>
      </div>
    </div>
  );
}
